import React, { useEffect, useRef, useState } from 'react';
import { loadConnectAndInitialize } from '@stripe/connect-js';
import { Box, Alert, CircularProgress, Typography } from '@mui/material';
import apiClient from '../../lib/api';

const StripeAccountOnboarding = ({
  accountId: propAccountId,
  onComplete,
  onError,
}) => {
  const containerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountId, setAccountId] = useState(propAccountId);
  const [showContinueButton, setShowContinueButton] = useState(false);
  const [needsAccountCreation, setNeedsAccountCreation] = useState(!propAccountId);

  const createAccount = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Creating new Stripe Connect account...');
      
      // Get current user info from localStorage
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;
      
      if (!user) {
        throw new Error('User not found');
      }
      
      const response = await apiClient.createStripeConnectAccount({
        businessType: 'company',
        businessDetails: {
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          address: {
            line1: '123 Main St', // Placeholder - Stripe will collect real address during onboarding
            city: 'City',
            state: 'CA',
            postalCode: '12345',
            country: 'US',
          },
        },
      });
      
      console.log('Stripe Connect account created:', response);
      const newAccountId = response.data.id;
      setAccountId(newAccountId);
      setNeedsAccountCreation(false);
      
      return newAccountId;
    } catch (err) {
      console.error('Error creating Stripe Connect account:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create Stripe account';
      setError(errorMessage);
      onError?.(errorMessage);
      setIsLoading(false);
      throw err;
    }
  };

  useEffect(() => {
    const initializeOnboarding = async () => {
      try {
        let currentAccountId = accountId;
        
        console.log('🔍 StripeAccountOnboarding useEffect triggered');
        console.log('🔍 Account ID:', currentAccountId);
        console.log('🔍 Publishable key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
        console.log('🔍 Needs account creation:', needsAccountCreation);
        
        // Create account if we don't have one (for DashboardLayout use case)
        if (!currentAccountId && needsAccountCreation) {
          currentAccountId = await createAccount();
        }
        
        // If we still don't have an account ID, we can't proceed
        if (!currentAccountId) {
          throw new Error('No account ID provided for onboarding');
        }
        
        console.log('Initializing Stripe onboarding for account:', currentAccountId);
        setIsLoading(true);
        setError(null);

        // Wait a bit to ensure the container is properly mounted
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check if publishable key is available
        if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
          throw new Error('Stripe publishable key is not configured');
        }

        // Initialize Stripe Connect with proper parameters
        console.log('Loading Stripe Connect with publishable key');
        const connectInstance = await loadConnectAndInitialize({
          publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
          fetchClientSecret: async () => {
            console.log('Fetching client secret for account:', currentAccountId);
            try {
              const response = await apiClient.createAccountSession(currentAccountId, {
                account_onboarding: { enabled: true },
                account_management: { enabled: true }
              });
              console.log('Client secret response:', response);
              
              if (!response.data?.client_secret) {
                throw new Error('No client secret returned from account session');
              }
              
              const clientSecret = response.data.client_secret;
              console.log('Client secret:', clientSecret);
              return clientSecret;
            } catch (error) {
              console.error('Error fetching client secret:', error);
              throw new Error(`Failed to create account session: ${error.message}`);
            }
          }
        });
        console.log('Stripe Connect instance created:', connectInstance);

        // Ensure container is available before creating component
        if (!containerRef.current) {
          throw new Error('Container not available for mounting');
        }

        // Create the onboarding component
        console.log('Creating account-onboarding component');
        const onboarding = connectInstance.create('account-onboarding');
        console.log('Onboarding component created:', onboarding);
        console.log('Onboarding component type:', typeof onboarding);

        // Add a fallback timeout in case the ready event doesn't fire
        const fallbackTimeout = setTimeout(() => {
          console.log('Stripe onboarding fallback timeout - hiding loading state');
          setIsLoading(false);
        }, 15000); // 15 second timeout

        // Set up event listeners BEFORE mounting
        onboarding.addEventListener('ready', () => {
          console.log('✅ Stripe onboarding ready event fired');
          clearTimeout(fallbackTimeout);
          setIsLoading(false);
        });

        onboarding.addEventListener('complete', () => {
          console.log('✅ Stripe onboarding complete event fired');
          clearTimeout(fallbackTimeout);
          onComplete?.(currentAccountId);
        });

        onboarding.addEventListener('error', (event) => {
          console.log('❌ Stripe onboarding error event fired:', event);
          clearTimeout(fallbackTimeout);
          const errorMessage = event.detail?.message || 'Onboarding failed';
          setError(errorMessage);
          onError?.(errorMessage);
          setIsLoading(false);
        });
        
        onboarding.addEventListener('exit', (event) => {
          console.log('🚪 Stripe onboarding exit event fired:', event);
          // Exit event fires when user finishes or closes the flow
          // Treat it as completion
          onComplete?.(currentAccountId);
        });
        
        onboarding.addEventListener('load', () => {
          console.log('📦 Stripe onboarding load event fired');
          // Hide loading immediately when content loads
          setIsLoading(false);
        });

        // Now mount the component
        console.log('Mounting onboarding component to container');
        console.log('Container ref:', containerRef.current);
        
        if (!containerRef.current) {
          throw new Error('Container not available for mounting');
        }
        
        containerRef.current.innerHTML = '';
        
        // Check if onboarding is an HTMLElement that can be appended
        if (onboarding && typeof onboarding.addEventListener === 'function') {
          containerRef.current.appendChild(onboarding);
          console.log('Onboarding component mounted successfully');
          
          // Hide loading after mounting as a backup if events don't fire
          setTimeout(() => {
            console.log('Backup: Hiding loading state 1 second after mount');
            setIsLoading(false);
          }, 1000);
        } else {
          console.error('Onboarding component is not a valid DOM element:', onboarding);
          throw new Error('Failed to mount onboarding component');
        }

      } catch (err) {
        console.error('Error in initializeOnboarding:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to initialize onboarding';
        setError(errorMessage);
        onError?.(errorMessage);
        setIsLoading(false);
      }
    };

    if (accountId || needsAccountCreation) {
      initializeOnboarding();
    }
  }, [accountId, needsAccountCreation]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      {isLoading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          p: 4,
        }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading Stripe onboarding...
          </Typography>
        </Box>
      )}
      
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%',
          position: 'relative',
          zIndex: 1
        }} 
      />
    </Box>
  );
};

export default StripeAccountOnboarding;
